/**
 * #2103 — sign the chat Firebase app out, and leave nothing behind.
 *
 * ══ WHY THIS IS ITS OWN MODULE WITH NO CS3 IMPORTS ══
 *
 * It is called from two places that must not import each other:
 *
 *   store/modules/user.js  the `logout` action — every logout goes through it
 *   chatService.js         `resetChatSession`, the panel's own teardown
 *
 * `chatService` imports `./api`, and `api.js` imports the store — so a store
 * module importing `chatService` would close a cycle (store → chatService →
 * api → store). Webpack tolerates that and then hands one of them a
 * half-initialised module at import time, which is the kind of failure that
 * only shows up in a production build. This file imports nothing but Firebase,
 * dynamically, so either side can call it.
 *
 * ══ ⚠ WHAT WAS ACTUALLY WRONG ══
 *
 * `signInWithCustomToken` with Firebase's DEFAULT persistence writes the signed
 * in user to IndexedDB (`firebase:authUser:<apiKey>:cs3-chat`), and nothing
 * removed it on logout. So after someone logged out of CS3 — on a shared
 * dispatch desk, say — the next person at that browser still held a valid
 * Firebase identity for the previous user and could read their Firestore chat
 * straight from devtools. The cs3 session was gone; the chat one was not.
 *
 * Two halves to the fix, and BOTH are needed:
 *
 *   1. new sign-ins use `inMemoryPersistence` (chatService.attachFirebase), so
 *      they end with the page;
 *   2. this signs out and deletes what EARLIER BUILDS already persisted —
 *      because a user who logged in before this ships has that IndexedDB entry
 *      today, and in-memory persistence from now on does not remove it.
 */

/** The named Firebase app chat uses. Must match `chatService.attachFirebase`. */
const APP_NAME = 'cs3-chat';

/**
 * Sign the chat app out, if it exists in this page.
 *
 * ⚠ NEVER THROWS, AND NEVER AWAITS ANYTHING THE CALLER NEEDS. It is called from
 * the logout path, which must complete whatever Firebase does — a logout that
 * fails because a chat SDK could not load is a user stuck signed in.
 */
export async function signOutChatFirebase() {
    try {
        const { getApps } = await import(/* webpackChunkName: "firebase" */ 'firebase/app');
        const app = getApps().find((a) => a.name === APP_NAME);
        if (!app) return false;

        const { getAuth, signOut } = await import(/* webpackChunkName: "firebase" */ 'firebase/auth');
        await signOut(getAuth(app));

        return true;
    } catch (e) {
        // No chat in this build, Firebase not loaded, or already signed out.
        return false;
    }
}

/**
 * Delete any `firebase:authUser` IndexedDB entry an EARLIER BUILD persisted for
 * the chat app.
 *
 * ⚠ THIS IS THE HALF THAT `inMemoryPersistence` CANNOT DO. Switching new
 * sign-ins to memory stops NEW entries being written; it does not remove the
 * one already sitting in the browser of everyone who used chat before this
 * ships. Without this, the fix would only protect people who had never used
 * the feature.
 *
 * Firebase's auth database is `firebaseLocalStorageDb`, one object store
 * (`firebaseLocalStorage`) keyed by `fbase_key`. Deleting the whole database
 * would also sign out the FCM app (`utils/firebase-messaging.js`, a different
 * project) — so only the chat app's own keys go, matched on the `:cs3-chat`
 * suffix Firebase appends for a named app.
 */
export function clearPersistedChatAuth(win) {
    const w = win || (typeof window === 'undefined' ? null : window);
    const idb = w && w.indexedDB;
    if (!idb) return Promise.resolve(false);

    return new Promise((resolve) => {
        let open;
        try {
            open = idb.open('firebaseLocalStorageDb');
        } catch (e) {
            resolve(false);

            return;
        }

        // Private modes and a locked-down browser both fail here; nothing to do.
        open.onerror = () => resolve(false);
        open.onblocked = () => resolve(false);

        open.onsuccess = () => {
            const db = open.result;
            try {
                if (!db.objectStoreNames.contains('firebaseLocalStorage')) {
                    db.close();
                    resolve(false);

                    return;
                }

                const tx = db.transaction('firebaseLocalStorage', 'readwrite');
                const store = tx.objectStore('firebaseLocalStorage');
                const keys = store.getAllKeys();

                keys.onsuccess = () => {
                    (keys.result || []).forEach((key) => {
                        /*
                         * `firebase:authUser:<apiKey>:cs3-chat`. The suffix is
                         * the APP NAME, which is why the chat app is named at
                         * all — the FCM app is the default one and its keys end
                         * in `:[DEFAULT]`, so this cannot touch it.
                         */
                        if (typeof key === 'string'
                            && key.indexOf('firebase:authUser:') === 0
                            && key.endsWith(':' + APP_NAME)) {
                            try { store.delete(key); } catch (e) { /* gone already */ }
                        }
                    });
                };

                tx.oncomplete = () => { db.close(); resolve(true); };
                tx.onerror = () => { db.close(); resolve(false); };
                tx.onabort = () => { db.close(); resolve(false); };
            } catch (e) {
                try { db.close(); } catch (e2) { /* nothing to close */ }
                resolve(false);
            }
        };
    });
}

/**
 * Everything a logout should do to chat: sign out, and remove what older builds
 * persisted.
 *
 * ⚠ ORDER MATTERS. `signOut` first, so Firebase's own listeners see the change
 * and stop; then delete whatever is left on disk. Doing it the other way round
 * lets Firebase rewrite the entry it had just loaded.
 */
export async function forgetChatIdentity(win) {
    const signedOut = await signOutChatFirebase();
    const cleared = await clearPersistedChatAuth(win);

    return { signedOut, cleared };
}
